import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Flag, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const REASONS = [
  { key: 'broken',        label: 'Broken / missing answer choices' },
  { key: 'wrong_answer',  label: 'Answer key looks wrong' },
  { key: 'inappropriate', label: 'Inappropriate content' },
  { key: 'confusing',     label: 'Confusing or unclear' },
  { key: 'other',         label: 'Something else' },
];

/**
 * Small "Report" flag button rendered next to any question. On click, opens
 * a modal to pick a reason + add optional detail. Reports notify platform
 * admins via the notification bell.
 */
const ReportQuestionButton = ({ question, subject, level, childId, childName, compact = false }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('broken');
  const [detail, setDetail] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post('/questions/report', {
        question_id: question?.id,
        question_text: question?.question || question?.text || 'Untitled question',
        subject,
        level,
        child_id: childId,
        child_name: childName,
        reason,
        detail: detail.trim() || null,
      });
      setSubmitted(true);
      toast.success('Report sent — thanks! Admins have been notified.');
      // Auto-close after a beat so parent can grab a snapshot of the "thanks" state
      setTimeout(() => { setOpen(false); setSubmitted(false); setDetail(''); setReason('broken'); }, 900);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Report failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Report this question"
        aria-label="Report this question"
        data-testid="report-question-btn"
        className={`${compact ? 'p-1' : 'px-2 py-1'} rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors text-xs inline-flex items-center gap-1`}
      >
        <Flag className="w-3.5 h-3.5" />
        {!compact && <span>Report</span>}
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? null : setOpen(false))}>
        <DialogContent className="sm:max-w-md" data-testid="report-question-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag className="w-5 h-5 text-rose-500" /> Report question</DialogTitle>
            <DialogDescription>
              Help us fix bad questions. Admins get pinged the moment you submit.
            </DialogDescription>
          </DialogHeader>
          {submitted ? (
            <div className="py-8 text-center">
              <div className="text-4xl">🎉</div>
              <p className="mt-2 font-semibold text-slate-900">Thanks — we&apos;re on it.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 line-clamp-3">
                {question?.question || question?.text || '(question text unavailable)'}
              </div>
              <div>
                <label className="text-sm font-medium">Reason</label>
                <div className="grid grid-cols-1 gap-1.5 mt-1.5">
                  {REASONS.map((r) => (
                    <label key={r.key} className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer text-sm ${reason === r.key ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="reason"
                        value={r.key}
                        checked={reason === r.key}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid={`report-reason-${r.key}`}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Anything else? (optional)</label>
                <Textarea
                  rows={3}
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  placeholder="E.g. the correct answer should be True, not False."
                  data-testid="report-detail-input"
                />
              </div>
            </div>
          )}
          {!submitted && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="report-submit-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Flag className="w-4 h-4 mr-1" />}
                Send report
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportQuestionButton;
